import { RIDE_STATUSES, isScoreSuppressed } from "score-common";
import { groupResultsByDivision, isResultRevealed, RESULT_STATUS } from "./orderOfGo";

// #3394 (Announcer Live Desk redesign, part 3) -- the one merged ride list that
// replaces the old RESULTS box + flat ride list. Pure ordering: no React, no
// fetch. The caller hands it the viewed class's ride rows (already in ride time
// order, buildOrderOfGoRows) and the joined results (buildClassResults).
//
// Three states, decided per class (and ranked per division):
//   NONE    no ride has a revealed score  -> ride time order
//   RANKING some scores in, class not placed -> scored rides first by rank
//           (grey, unofficial), then the rest in ride time order
//   PLACED  the class is placed (the ring-wide summary says so, OR every scored
//           ride is FINAL: LookupColumn resolves `viewedPlaced`, summary OR isClassResultsFinal, and passes it through)
//           AND at least one ride has a stored placing -> by placing (orange),
//           then unplaced rides in ride time order. `placed` with no stored
//           placing yet (the summary can flip a refresh before FINAL rows land)
//           falls back to RANKING/NONE, so scored rides keep their ranks.
// A scratched ride always sinks to the bottom of ITS OWN division (there is no
// Scratched group). Only a CONFIRMED/FINAL result is ever ranked: a PARTIAL
// result carries no composite (anti-leak, #699), so it cannot rank.

export const LIST_MODE = { NONE: "NONE", RANKING: "RANKING", PLACED: "PLACED" };
export const PLACE_KIND = { PLACING: "placing", RANK: "rank" };

function isScratched(row, result) {
  return row.status === RIDE_STATUSES.SCRATCHED || result?.status === RESULT_STATUS.SCRATCHED;
}

function isRankable(entry) {
  const { result, row } = entry;
  return (
    !entry.scratched &&
    Boolean(result) &&
    isResultRevealed(result.status) &&
    Number.isFinite(result.composite) &&
    !isScoreSuppressed(row.termReason)
  );
}

function hasPlacing(entry) {
  return !entry.scratched && Number.isFinite(Number(entry.result?.placing)) && Number(entry.result?.placing) > 0;
}

// Orders ONE division (or the whole flat class). `entries` arrive in ride time
// order and Array.prototype.sort is stable, so ties keep ride time order.
function orderEntries(entries, placed) {
  const scratched = entries.filter((e) => e.scratched);
  const live = entries.filter((e) => !e.scratched);
  let head = [];
  let tail = live;
  if (placed) {
    head = live.filter(hasPlacing).sort((a, b) => Number(a.result.placing) - Number(b.result.placing));
    tail = live.filter((e) => !hasPlacing(e));
    for (const e of head) {
      e.place = Number(e.result.placing);
      e.placeKind = PLACE_KIND.PLACING;
    }
  } else {
    head = live.filter(isRankable).sort((a, b) => b.result.composite - a.result.composite);
    tail = live.filter((e) => !isRankable(e));
    // Competition ranking: equal composites share a rank (1, 1, 3).
    head.forEach((e, i) => {
      const prev = head[i - 1];
      e.place = prev && prev.result.composite === e.result.composite ? prev.place : i + 1;
      e.placeKind = PLACE_KIND.RANK;
    });
  }
  return [...head, ...tail, ...scratched];
}

/**
 * @returns {{ mode: string, groups: Array<{token: string, label: string|null, entries: object[]}>|null, entries: object[] }}
 *   `groups` is null when the class is not placedByDivision (or has only one division): render flat
 *   from `entries`. Otherwise `groups` holds every entry and `entries` is [].
 */
export function buildLookupRideList({ rideRows = [], results = [], placed = false, currentRideId = null, placedByDivision = false }) {
  const resultByRide = new Map((results || []).map((r) => [r.rideId, r]));
  const all = (rideRows || []).map((row) => {
    const result = resultByRide.get(row.id) || null;
    return {
      row,
      result,
      // groupResultsByDivision keys off `riderStatus`, but only for a class with placedByDivision (#3407).
      riderStatus: row.riderStatus || "",
      scratched: isScratched(row, result),
      isCurrent: row.id === currentRideId,
      place: null,
      placeKind: null,
    };
  });

  const anyRanked = all.some(isRankable);
  const anyPlaced = placed && all.some(hasPlacing);
  const mode = anyPlaced ? LIST_MODE.PLACED : anyRanked ? LIST_MODE.RANKING : LIST_MODE.NONE;
  const usePlaced = mode === LIST_MODE.PLACED;
  // NONE falls out of orderEntries: nothing to rank, so ride time order, scratches last.
  const order = (list) => orderEntries(list, usePlaced);

  const divisionGroups = groupResultsByDivision(all, { placedByDivision });
  if (!divisionGroups) return { mode, groups: null, entries: order(all) };
  return {
    mode,
    groups: divisionGroups.map((g) => ({ token: g.token, label: g.label, entries: order(g.rows) })),
    entries: [],
  };
}
